import { FocusEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** What leaving the box did to the number that was typed, and why. */
type Adjustment = {
      typed: number;
      settled: number;
      reason: "range" | "step";
};

// How long the "why did my number change" line stays up.
const ADJUSTMENT_NOTICE_MS = 4000;

interface SettledNumberOptions
{
      /** The committed value the field is showing. */
      value: number | null | undefined;
      min?: number;
      max?: number;
      step?: number;
      /** Slider-backed fields can only stand on the step grid; plain boxes can't. */
      snapToStep?: boolean;
      onChange: (val: number) => void;
}

/**
 * The bookkeeping a number box needs to stop silently swallowing what was typed.
 *
 * AntD's InputNumber withholds `onChange` entirely while the typed value sits
 * outside [min, max], then quietly restores the last valid one on blur. From the
 * outside that reads as "I changed the value, then it jumped back on its own" —
 * so anything the box refuses is reported here instead: the bounds are stated up
 * front, and a value that got moved says what happened to it.
 *
 * Shared by the slider field and the plain number field so both explain
 * themselves the same way.
 */
export const useSettledNumber = ({
      value,
      min,
      max,
      step,
      snapToStep = false,
      onChange,
}: SettledNumberOptions) =>
{
      const { t } = useTranslation();

      // What the box currently shows. It tracks `value` but can hold an empty box
      // mid-typing (clearing before retyping) without pushing that emptiness out.
      const [draft, setDraft] = useState<number | null>(value ?? null);

      const [adjustment, setAdjustment] = useState<Adjustment | null>(null);

      useEffect(() => setDraft(value ?? null), [value]);

      useEffect(() =>
      {
            if (!adjustment) return;

            const timer = setTimeout(() => setAdjustment(null), ADJUSTMENT_NOTICE_MS);
            return () => clearTimeout(timer);
      }, [adjustment]);

      const lower = min ?? Number.NEGATIVE_INFINITY;
      const upper = max ?? Number.POSITIVE_INFINITY;

      /** Where the field is able to stand: inside the bounds, and on the grid. */
      const settle = (raw: number) =>
      {
            const clamped = Math.min(upper, Math.max(lower, raw));

            if (!snapToStep || !step || step <= 0 || !Number.isFinite(lower))
            {
                  return clamped;
            }

            const grid = lower + Math.round((clamped - lower) / step) * step;
            // toFixed absorbs the float drift a decimal step leaves behind.
            const snapped = Math.min(upper, Math.max(lower, Number(grid.toFixed(6))));

            // The grid doesn't always reach max (min 0, step 7 stops at 98), and a
            // slider takes the max endpoint anyway — so max stays available
            // whenever it's the closer of the two, which also keeps this agreeing
            // with the clamp AntD applies to an out-of-range entry.
            return Math.abs(clamped - snapped) <= Math.abs(clamped - upper)
                  ? snapped
                  : upper;
      };

      const handleChange = (val: number | null) =>
      {
            setDraft(val);

            // Whatever arrives here is already inside the bounds — AntD held back
            // anything else. Only the step grid still has to be settled, and that
            // waits for blur too: snapping per keystroke would rewrite "4" to 5
            // before the rest of "42" could be typed.
            if (val === null || Number.isNaN(val)) return;

            onChange(val);
      };

      // Read straight off the input: an out-of-range entry never reaches `draft`,
      // because AntD holds it back until blur.
      const handleBlur = (event: FocusEvent<HTMLInputElement>) =>
      {
            const raw = event.target.value?.trim();
            const typed = raw ? Number(raw) : NaN;

            if (Number.isNaN(typed))
            {
                  // Left empty — put the committed value back rather than leaving
                  // the box disagreeing with what the form holds.
                  setDraft(value ?? null);
                  return;
            }

            const settled = settle(typed);

            setDraft(settled);
            if (settled !== value) onChange(settled);

            setAdjustment(
                  settled === typed
                        ? null
                        : {
                              typed,
                              settled,
                              reason: typed < lower || typed > upper ? "range" : "step",
                        },
            );
      };

      // Stated up front, because these are the rules that decide what a typed
      // number settles on — a field that only explains itself after the fact reads
      // as broken. Whole sentences per case rather than glued-together fragments,
      // so each one stays translatable. Nothing is claimed when the schema sets no
      // bounds.
      const hasBounds = min !== undefined && max !== undefined;

      const hint = !hasBounds
            ? null
            : snapToStep && step && step > 1
                  ? t("Range {{min}}–{{max}}, steps of {{step}}", { min, max, step })
                  : t("Range {{min}}–{{max}}", { min, max });

      const notice = adjustment
            ? adjustment.reason === "range"
                  ? t("{{typed}} is outside {{min}}–{{max}}, set to {{value}}", {
                        typed: adjustment.typed,
                        min,
                        max,
                        value: adjustment.settled,
                  })
                  : t("{{typed}} rounded to {{value}} (steps of {{step}})", {
                        typed: adjustment.typed,
                        value: adjustment.settled,
                        step,
                  })
            : null;

      return {
            draft,
            handleChange,
            handleBlur,
            clearAdjustment: () => setAdjustment(null),
            hint,
            notice,
      };
};

export default useSettledNumber;
